import type { RendererProps } from "../core/element-registry"
import { ElementRenderer } from "./element-renderer"
import {
  InputGroup,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupAddon,
} from "../components/ui/input-group"
import { cn } from "../lib/utils"
import { useRenderContext } from "../core/render-context"

export function InputGroupRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  return (
    <InputGroup
      className={cn(element.className)}
      data-testid={`input-group-${element.id}`}
    >
      {element.children?.map((child) => {
        const childType = child.type

        if (childType === "button") {
          const label = child.text
            ? String(resolveSymbol(child.text) ?? "")
            : undefined
          return (
            <InputGroupButton key={child.id} className={cn(child.className)}>
              {label}
            </InputGroupButton>
          )
        }

        if (childType === "input") {
          const placeholder = child.placeholder
            ? String(resolveSymbol(child.placeholder) ?? "")
            : undefined
          const rawVal = (child as any).value ?? (child as any).defaultValue
          const defaultValue = rawVal !== undefined
            ? String(resolveSymbol(rawVal) ?? rawVal)
            : undefined
          return (
            <InputGroupInput
              key={child.id}
              placeholder={placeholder}
              defaultValue={defaultValue}
              readOnly={(child as any).readonly === true}
              className={cn(child.className)}
            />
          )
        }

        if (childType === "input-group-addon") {
          const align = (child as any).align as "inline-start" | "inline-end" | undefined
          return (
            <InputGroupAddon key={child.id} align={align} className={cn(child.className)}>
              {child.children?.map((gc) => (
                <ElementRenderer key={gc.id} element={gc} />
              ))}
            </InputGroupAddon>
          )
        }

        if (childType === "icon" || childType === "kbd") {
          // Icons and kbd render as InputGroupAddon (matching shadcn v4 pattern)
          const align = child.align as "inline-start" | "inline-end" | undefined
          return (
            <InputGroupAddon key={child.id} align={align} className={cn(child.className)}>
              <ElementRenderer element={child} />
            </InputGroupAddon>
          )
        }

        if (childType === "text") {
          return (
            <InputGroupText key={child.id} className={cn(child.className)}>
              <ElementRenderer element={child} />
            </InputGroupText>
          )
        }

        // Fallback: render as addon
        return (
          <InputGroupAddon key={child.id} className={cn(child.className)}>
            <ElementRenderer element={child} />
          </InputGroupAddon>
        )
      })}
    </InputGroup>
  )
}
